import { useCallback, useRef, useState } from 'react';
import type { tFieldElement } from './FieldCharCounter.types';

/**
 * Pure predicate: returns true when the in-flight paste event would push the
 * field's value past `max` (after replacing any selected range). Useful for
 * components that already manage their own error/warning state and just need
 * the overflow detection.
 */
export function wouldPasteOverflow(
  event: React.ClipboardEvent<tFieldElement>,
  max: number,
): boolean {
  const field = event.currentTarget;
  const pasted = event.clipboardData?.getData('text') ?? '';
  const currentLength = field.value.length;
  const selectionStart = field.selectionStart ?? currentLength;
  const selectionEnd = field.selectionEnd ?? currentLength;
  const selectedLength = Math.max(0, selectionEnd - selectionStart);
  return currentLength - selectedLength + pasted.length > max;
}

/**
 * Detects when a paste into a capped text field will be silently truncated
 * by the browser's `maxLength` enforcement, so callers can surface an inline
 * warning to the user.
 *
 * Usage:
 *   const paste = usePasteTruncationWarning(MAX);
 *   <textarea
 *     maxLength={MAX}
 *     onChange={(e) => { paste.clear(); setValue(e.target.value); }}
 *     onPaste={paste.onPaste}
 *   />
 *   <PasteTruncatedWarning visible={paste.truncated} max={MAX} />
 */
export function usePasteTruncationWarning(max: number) {
  const [truncated, setTruncated] = useState(false);
  // A paste that overflows is immediately followed by the browser's own `input`
  // event, which reaches the field's onChange and would clear the warning in the
  // same task -- before any paint. Latch here so that one follow-up clear is
  // swallowed; every later edit still clears a stale warning as intended.
  const skipNextClearRef = useRef(false);

  const onPaste = useCallback(
    (event: React.ClipboardEvent<tFieldElement>) => {
      // Set unconditionally so a follow-up paste that fits clears a stale
      // warning from a prior overflowing paste, even when no onChange has
      // fired in between.
      const overflow = wouldPasteOverflow(event, max);
      skipNextClearRef.current = overflow;
      setTruncated(overflow);
    },
    [max],
  );

  const clear = useCallback(() => {
    if (skipNextClearRef.current) {
      skipNextClearRef.current = false;
      return;
    }
    setTruncated(false);
  }, []);

  return { onPaste, truncated, clear };
}
